import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThreeDots } from "react-loader-spinner";
import { toast, Slide as ToastSlide } from "react-toastify";
import cx from "classnames";

import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import { Search, ExpandMore, ExpandLess } from "@material-ui/icons";
import Save from "@material-ui/icons/Save";

import Card from "../../../components/Card/Card.js";
import CardBody from "../../../components/Card/CardBody.js";
import CardHeader from "../../../components/Card/CardHeader.js";
import CardIcon from "../../../components/Card/CardIcon.js";
import Button from "../../../components/CustomButtons/Button.js";
import GridContainer from "../../../components/Grid/GridContainer.js";
import GridItem from "../../../components/Grid/GridItem.js";
import Assignment from "@material-ui/icons/Assignment";

import useThemeColors from "../../../hooks/useThemeColors";
import usePermissions from "../../../hooks/usePermissions";
import { client } from "../../../services/axiosClient";
import {
  getUsers,
  getPresets,
  getPermissionsCatalog,
  bulkSave,
} from "../../../services/accessService";
import ScopeDialog from "./ScopeDialog";
import AuditLogDialog from "./AuditLogDialog";
import ConfirmDangerousDialog from "./ConfirmDangerousDialog";
import styles from "../../../assets/jss/material-dashboard-pro-react/views/accessManagementStyle.js";

const useStyles = makeStyles(styles);

const BLOCK_ORDER = [
  "overview",
  "modeling",
  "results",
  "governance",
  "admin",
  "ai",
];

const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));
const sortedJoin = (arr) => [...(arr || [])].sort().join("|");

export default function AccessManagement() {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientColor } = useThemeColors();
  const { can } = usePermissions();

  const canManage = can("admin.users.manage");
  // O atomo admin.users.assign_permissions foi fundido em admin.users.manage:
  // quem gerencia usuarios tambem atribui permissoes.
  const canAssign = canManage;

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]); // copia de trabalho (editavel)
  const [baseline, setBaseline] = useState([]); // snapshot do servidor
  const [presets, setPresets] = useState([]);
  const [catalog, setCatalog] = useState({ atoms: [] });
  const [search, setSearch] = useState("");
  const [presetFilter, setPresetFilter] = useState("all");
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkPreset, setBulkPreset] = useState("");
  const [bulkCopySource, setBulkCopySource] = useState("");
  const [scopeUser, setScopeUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  // Mapa dimensao(coluna BQ) -> label amigavel, para a coluna ESCOPO exibir
  // o nome de exibicao (ex.: "Zona") em vez do campo cru (ex.: "zona").
  const [dimNames, setDimNames] = useState({});
  const [confirmGrants, setConfirmGrants] = useState(null); // null = fechado

  const notify = (msg, type = "info") =>
    toast(msg, {
      position: "top-right",
      autoClose: 2500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      theme: "light",
      type,
      transition: ToastSlide,
    });

  // ------------------------------------------------------------------
  // Load
  // ------------------------------------------------------------------
  const loadAll = async () => {
    setLoading(true);
    try {
      const [usersResp, presetsResp, catalogResp] = await Promise.all([
        getUsers(),
        getPresets(),
        getPermissionsCatalog(),
      ]);
      const loaded = usersResp.users || [];
      setUsers(loaded);
      setBaseline(deepCopy(loaded));
      setPresets(presetsResp.presets || []);
      setCatalog(catalogResp || { atoms: [] });
    } catch (e) {
      notify(t("access_management.load_error"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) loadAll();
  }, [canManage]); // eslint-disable-line

  // Carrega os nomes amigaveis das dimensoes (mesmo metadado do ScopeDialog/
  // DataExplorer) para rotular a coluna ESCOPO. Best-effort: se falhar, a
  // coluna cai para a chave crua.
  useEffect(() => {
    if (!canManage) return;
    client
      .post("api/data_explorer_meta/")
      .then((resp) => {
        const f = resp.data?.filters || {};
        const names = {};
        (f.list || []).forEach((k) => {
          names[k] = f.names?.[k]?.[i18n.language] || k;
        });
        setDimNames(names);
      })
      .catch(() => {});
  }, [canManage, i18n.language]);

  // ------------------------------------------------------------------
  // Maps derivados
  // ------------------------------------------------------------------
  const presetByCode = useMemo(() => {
    const m = {};
    presets.forEach((p) => {
      m[p.code] = p;
    });
    return m;
  }, [presets]);

  // Assinatura (conjunto ordenado de codes) -> preset code. Permite "encaixar"
  // de volta num preset quando as permissoes voltam a bater exatamente com ele.
  const presetBySignature = useMemo(() => {
    const m = {};
    presets.forEach((p) => {
      m[[...p.permissions].sort().join("|")] = p.code;
    });
    return m;
  }, [presets]);

  // Dado um conjunto de codes, retorna o preset que casa exatamente, ou "".
  const detectPresetCode = (codes) =>
    presetBySignature[[...codes].sort().join("|")] || "";

  const baselineMap = useMemo(() => {
    const m = {};
    baseline.forEach((u) => {
      m[u.user_id] = u;
    });
    return m;
  }, [baseline]);

  const isUserDirty = (u) => {
    const b = baselineMap[u.user_id];
    if (!b) return false;
    return (
      sortedJoin(u.permissions) !== sortedJoin(b.permissions) ||
      JSON.stringify(u.restriction_filters || {}) !==
        JSON.stringify(b.restriction_filters || {}) ||
      (u.applied_preset_code || "") !== (b.applied_preset_code || "")
    );
  };

  const dirtyUsers = useMemo(
    () => users.filter(isUserDirty),
    [users, baselineMap], // eslint-disable-line
  );
  const hasChanges = dirtyUsers.length > 0;

  // Catalogo plano -> mapa por code + mapas de dependencia (requires/dependents)
  const atomByCode = useMemo(() => {
    const m = {};
    (catalog.atoms || []).forEach((a) => {
      m[a.code] = a;
    });
    return m;
  }, [catalog]);

  // Estrutura da tabela: bloco "Paginas" (navegacao) + 1 bloco por modulo (acoes).
  // Cada bloco tem grupos (sub-blocos). Grupo com >1 atomo ganha coluna agregada.
  const tableBlocks = useMemo(() => {
    const atoms = catalog.atoms || [];
    const nav = atoms.filter((a) => a.layer === "navigation");
    const actions = atoms.filter((a) => a.layer === "action");

    // Paginas: agrupa navegacao por "block" (modeling vira sub-grupo "Modelado")
    const navByBlock = {};
    nav.forEach((a) => {
      (navByBlock[a.block] = navByBlock[a.block] || []).push(a);
    });
    const pagesGroups = Object.keys(navByBlock)
      .sort((x, y) => BLOCK_ORDER.indexOf(x) - BLOCK_ORDER.indexOf(y))
      .map((b) => ({ key: b, labelKind: "block", atoms: navByBlock[b] }));

    // Modulos: agrupa acoes por block -> sub_block
    const actByBlock = {};
    actions.forEach((a) => {
      actByBlock[a.block] = actByBlock[a.block] || {};
      (actByBlock[a.block][a.sub_block] =
        actByBlock[a.block][a.sub_block] || []).push(a);
    });
    const moduleBlocks = Object.keys(actByBlock)
      .sort((x, y) => BLOCK_ORDER.indexOf(x) - BLOCK_ORDER.indexOf(y))
      .map((b) => ({
        key: b,
        kind: "module",
        groups: Object.keys(actByBlock[b]).map((sb) => ({
          key: sb,
          labelKind: "sub_block",
          atoms: actByBlock[b][sb],
        })),
      }));

    return [
      { key: "pages", kind: "pages", groups: pagesGroups },
      ...moduleBlocks,
    ];
  }, [catalog]);

  // Colunas visiveis por bloco: expandido -> agregado(>1) + atomos; colapsado -> resumo
  const blockColumns = (block) => {
    if (!expandedBlocks[block.key]) {
      const all = block.groups.flatMap((g) => g.atoms);
      return [{ type: "summary", block: block.key, atoms: all }];
    }
    const cols = [];
    block.groups.forEach((g) => {
      if (g.atoms.length > 1) {
        cols.push({ type: "agg", block: block.key, group: g });
      }
      g.atoms.forEach((atom) =>
        cols.push({ type: "atom", block: block.key, group: g, atom }),
      );
    });
    return cols;
  };

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (presetFilter !== "all" && u.applied_preset_code !== presetFilter) {
        return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (
          !(u.name || "").toLowerCase().includes(q) &&
          !(u.email || "").toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [users, presetFilter, search]);

  // ------------------------------------------------------------------
  // Mutacoes LOCAIS (sem API)
  // ------------------------------------------------------------------
  const toggleBlock = (blockKey) =>
    setExpandedBlocks((prev) => ({ ...prev, [blockKey]: !prev[blockKey] }));

  const expandAll = () => {
    const all = {};
    tableBlocks.forEach((b) => {
      all[b.key] = true;
    });
    setExpandedBlocks(all);
  };
  const collapseAll = () => setExpandedBlocks({});

  const applyPresetLocal = (userId, presetCode) => {
    if (!canAssign) return;
    const preset = presetByCode[presetCode];
    const atoms = preset ? [...preset.permissions] : [];
    setUsers((prev) =>
      prev.map((u) =>
        u.user_id === userId
          ? { ...u, permissions: atoms, applied_preset_code: presetCode }
          : u,
      ),
    );
  };

  const mutateUserPerms = (userId, mutator) => {
    if (!canAssign) return;
    setUsers((prev) =>
      prev.map((u) => {
        if (u.user_id !== userId) return u;
        const set = new Set(u.permissions);
        mutator(set);
        const codes = Array.from(set);
        return {
          ...u,
          permissions: codes,
          // Se o conjunto resultante casar com um preset, "encaixa" de volta
          // nele; senao, vira Personalizado ("").
          applied_preset_code: detectPresetCode(codes),
        };
      }),
    );
  };

  const toggleAtomLocal = (userId, atomCode, currentlyChecked) => {
    mutateUserPerms(userId, (set) => {
      if (currentlyChecked) set.delete(atomCode);
      else set.add(atomCode);
    });
  };

  // Toggle agregado de um sub-bloco: marca/desmarca todos os atomos do grupo.
  const toggleGroupLocal = (userId, group, allChecked) => {
    mutateUserPerms(userId, (set) => {
      group.atoms.forEach((a) => {
        if (allChecked) set.delete(a.code);
        else set.add(a.code);
      });
    });
  };

  const saveScopeLocal = (filters) => {
    if (!scopeUser) return;
    setUsers((prev) =>
      prev.map((u) =>
        u.user_id === scopeUser.user_id
          ? { ...u, restriction_filters: filters }
          : u,
      ),
    );
    setScopeUser(null);
  };

  // Selecao de origem no bulk — preset e copiar-de sao mutuamente exclusivos
  const chooseBulkPreset = (code) => {
    setBulkPreset(code);
    if (code) setBulkCopySource("");
  };
  const chooseBulkCopySource = (userId) => {
    setBulkCopySource(userId);
    if (userId) setBulkPreset("");
  };

  const applyBulk = () => {
    if (!canAssign || selectedUserIds.length === 0) return;

    if (bulkPreset) {
      const preset = presetByCode[bulkPreset];
      const atoms = preset ? [...preset.permissions] : [];
      setUsers((prev) =>
        prev.map((u) =>
          selectedUserIds.includes(u.user_id)
            ? { ...u, permissions: atoms, applied_preset_code: bulkPreset }
            : u,
        ),
      );
    } else if (bulkCopySource) {
      const source = users.find((u) => u.user_id === bulkCopySource);
      if (!source) return;
      setUsers((prev) =>
        prev.map((u) =>
          selectedUserIds.includes(u.user_id) && u.user_id !== source.user_id
            ? {
                ...u,
                permissions: [...source.permissions],
                restriction_filters: {
                  ...(source.restriction_filters || {}),
                },
                applied_preset_code: detectPresetCode(source.permissions),
              }
            : u,
        ),
      );
    }

    setSelectedUserIds([]);
    setBulkPreset("");
    setBulkCopySource("");
  };

  const clearBulk = () => {
    setSelectedUserIds([]);
    setBulkPreset("");
    setBulkCopySource("");
  };

  const toggleSelectUser = (userId) =>
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );

  // ------------------------------------------------------------------
  // Save (persiste tudo de uma vez)
  // ------------------------------------------------------------------
  // Concessoes perigosas pendentes: atomos is_dangerous que o usuario GANHOU
  // (estao no estado atual mas nao no baseline).
  const computeDangerousGrants = () => {
    const grants = [];
    dirtyUsers.forEach((u) => {
      const before = new Set(baselineMap[u.user_id]?.permissions || []);
      const gainedDangerous = (u.permissions || []).filter(
        (code) =>
          !before.has(code) &&
          atomByCode[code] &&
          atomByCode[code].is_dangerous,
      );
      if (gainedDangerous.length > 0) {
        grants.push({
          userName: u.name,
          permLabels: gainedDangerous.map((code) =>
            t(atomByCode[code].label_i18n_key, code),
          ),
        });
      }
    });
    return grants;
  };

  const doSave = async () => {
    const changes = dirtyUsers.map((u) => ({
      user_id: u.user_id,
      permissions: u.permissions,
      restriction_filters: u.restriction_filters || {},
      applied_preset_code: u.applied_preset_code || "",
    }));
    setSaving(true);
    try {
      const resp = await bulkSave(changes);
      const updatedById = {};
      (resp.users || []).forEach((x) => {
        updatedById[x.user_id] = x;
      });
      const merged = users.map((u) => updatedById[u.user_id] || u);
      setUsers(merged);
      setBaseline(deepCopy(merged));
      notify(t("access_management.saved"), "info");
    } catch (e) {
      notify(t("access_management.save_error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    const grants = computeDangerousGrants();
    if (grants.length > 0) {
      setConfirmGrants(grants); // abre 2-step confirm
      return;
    }
    await doSave();
  };

  const discardChanges = () => {
    setUsers(deepCopy(baseline));
    setSelectedUserIds([]);
    setBulkPreset("");
  };

  // ------------------------------------------------------------------
  // Render helpers
  // ------------------------------------------------------------------
  const presetLabel = (code) =>
    code
      ? t(`access_management.presets.${code}`, code)
      : t("access_management.custom");

  const scopeSummary = (filters) => {
    const keys = Object.keys(filters || {});
    if (keys.length === 0) return "—";
    return keys
      .map((k) => `${dimNames[k] || k}: ${(filters[k] || []).length}`)
      .join(" · ");
  };

  const blockSummaryMark = (atoms, permSet) => {
    const total = atoms.length;
    const owned = atoms.filter((a) => permSet.has(a.code)).length;
    if (total === 0 || owned === 0)
      return { text: "—", cls: classes.summaryEmpty };
    if (owned === total)
      return { text: `${owned}/${total}`, cls: classes.summaryFull };
    return { text: `${owned}/${total}`, cls: classes.summaryPartial };
  };

  const blockLabel = (blockKey) =>
    t(`access_management.blocks.${blockKey}`, blockKey);

  const groupHeaderLabel = (group) =>
    group.labelKind === "block"
      ? t(`access_management.blocks.${group.key}`, group.key)
      : t(`access_management.sub_blocks.${group.key}`, group.key);

  const aggState = (group, permSet) => {
    const owned = group.atoms.filter((a) => permSet.has(a.code)).length;
    return {
      all: owned === group.atoms.length,
      some: owned > 0 && owned < group.atoms.length,
    };
  };

  // Colunas por bloco (depende de expandedBlocks) — calculado no render.
  const blocksRender = tableBlocks.map((b) => ({
    block: b,
    columns: blockColumns(b),
  }));

  if (!canManage) {
    return <h1>{t("access_management.unauthorized")}</h1>;
  }

  return (
    <GridContainer style={{ marginTop: "-40px" }}>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="client" icon>
            <CardIcon color="client">
              <Assignment />
            </CardIcon>
            <h4
              className={classes.cardIconTitle}
              style={{ marginBottom: "0px !important" }}
            >
              {t("access_management.title")}
            </h4>
            <GridContainer
              justify="flex-end"
              style={{ position: "absolute", top: "-5px" }}
            >
              <GridItem
                style={{ position: "absolute", right: "35px", top: "10px" }}
              >
                {saving && (
                  <ThreeDots
                    color={clientColor}
                    height={20}
                    width={20}
                    ariaLabel="loading"
                  />
                )}
              </GridItem>
              <GridItem>
                <Button
                  justIcon
                  color="client"
                  className={cx({
                    [classes.cardButton]: true,
                    [classes.attention]: hasChanges || saving,
                    [classes.loadingAnimation]: !saving,
                    [classes.actionDisabled]: !hasChanges || saving,
                  })}
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                >
                  <Save className={classes.cardButtonIcon} />
                </Button>
              </GridItem>
            </GridContainer>
          </CardHeader>
          <CardBody>
            {/* Toolbar */}
            <div className={classes.toolbar}>
              {/* Honeypot anti-autofill: campo "fake" antes do real para o
                  navegador/gerenciador de senha escolher esse em vez do
                  campo de busca (mesma tecnica do InputsV3). */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: -10000,
                  top: -10000,
                  width: 1,
                  height: 1,
                  opacity: 0,
                }}
                readOnly
              />
              <TextField
                className={classes.searchField}
                placeholder={t("access_management.search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
                name={`access-search-${Math.random().toString(36).slice(2, 8)}`}
                onFocus={(e) => e.target.removeAttribute("readonly")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" htmlColor="#b0b4bd" />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  // Camada extra: readOnly no mount (Chrome ignora autofill em
                  // readOnly), liberado no primeiro focus; + atributos que
                  // desligam 1Password/LastPass/nativos.
                  readOnly: true,
                  autoComplete: "new-password",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  "data-1p-ignore": "true",
                  role: "presentation",
                  spellCheck: "false",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                }}
              />
              <FormControl
                className={cx(classes.selectFormControl, classes.filterSelect)}
              >
                <Select
                  value={presetFilter}
                  onChange={(e) => setPresetFilter(e.target.value)}
                  displayEmpty
                  className={classes.filterSelectText}
                  MenuProps={{ className: classes.selectMenu }}
                >
                  <MenuItem
                    value="all"
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                  >
                    {t("access_management.filter_all_presets")}
                  </MenuItem>
                  {presets.map((p) => (
                    <MenuItem
                      key={p.code}
                      value={p.code}
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                    >
                      {presetLabel(p.code)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div className={classes.toolbarSpacer} />
              <Button
                simple
                size="sm"
                className={classes.toolbarTextButton}
                onClick={() => setAuditOpen(true)}
              >
                {t("access_management.audit.open")}
              </Button>
              <Button
                simple
                size="sm"
                className={classes.toolbarTextButton}
                onClick={expandAll}
              >
                {t("access_management.expand_all")}
              </Button>
              <Button
                simple
                size="sm"
                className={classes.toolbarTextButton}
                onClick={collapseAll}
              >
                {t("access_management.collapse_all")}
              </Button>
              {hasChanges && (
                <Button
                  simple
                  size="sm"
                  className={classes.toolbarTextButton}
                  onClick={discardChanges}
                  disabled={saving}
                >
                  {t("access_management.discard")} ({dirtyUsers.length})
                </Button>
              )}
            </div>

            {loading ? (
              <div className={classes.spinnerWrap}>
                <ThreeDots color={clientColor} height={70} width={70} />
              </div>
            ) : (
              <div className={classes.tableWrapper}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.userColHeader} rowSpan={2}>
                        {t("access_management.columns.user")}
                      </th>
                      <th className={classes.presetColHeader} rowSpan={2}>
                        {t("access_management.columns.preset")}
                      </th>
                      {blocksRender.map(({ block, columns }) => {
                        const expanded = expandedBlocks[block.key];
                        return (
                          <th
                            key={block.key}
                            className={classes.groupHeaderCell}
                            colSpan={columns.length}
                            onClick={() => toggleBlock(block.key)}
                          >
                            <span className={classes.groupHeaderInner}>
                              {blockLabel(block.key)}
                              {expanded ? (
                                <ExpandLess fontSize="inherit" />
                              ) : (
                                <ExpandMore fontSize="inherit" />
                              )}
                            </span>
                          </th>
                        );
                      })}
                      <th className={classes.scopeHeaderCell} rowSpan={2}>
                        {t("access_management.columns.scope")}
                      </th>
                    </tr>
                    <tr>
                      {blocksRender.flatMap(({ columns }) =>
                        columns.map((col) => {
                          if (col.type === "summary") {
                            return (
                              <th
                                key={`sum-${col.block}`}
                                className={classes.atomHeaderCell}
                              >
                                &nbsp;
                              </th>
                            );
                          }
                          if (col.type === "agg") {
                            return (
                              <th
                                key={`agg-${col.block}-${col.group.key}`}
                                className={cx(
                                  classes.atomHeaderCell,
                                  classes.aggHeaderCell,
                                  classes.groupStartCell,
                                )}
                              >
                                {groupHeaderLabel(col.group)}
                              </th>
                            );
                          }
                          const atom = col.atom;
                          const singleInGroup =
                            col.group && col.group.atoms.length === 1;
                          return (
                            <th
                              key={atom.code}
                              className={cx(classes.atomHeaderCell, {
                                [classes.dangerHeader]: atom.is_dangerous,
                                [classes.groupStartCell]: singleInGroup,
                              })}
                            >
                              <Tooltip
                                title={
                                  <div>
                                    <div className={classes.permTooltipTitle}>
                                      {t(atom.label_i18n_key, atom.code)}
                                    </div>
                                    <div className={classes.permTooltipDesc}>
                                      {t(atom.description_i18n_key, "")}
                                    </div>
                                  </div>
                                }
                              >
                                <span>
                                  {t(
                                    atom.label_i18n_key,
                                    atom.code.split(".").pop(),
                                  )}
                                </span>
                              </Tooltip>
                            </th>
                          );
                        }),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => {
                      const permSet = new Set(u.permissions);
                      const dirty = isUserDirty(u);
                      return (
                        <tr key={u.user_id} className={classes.bodyRow}>
                          <td
                            className={cx(classes.userCell, {
                              [classes.dirtyUserCell]: dirty,
                            })}
                          >
                            <Checkbox
                              checked={selectedUserIds.includes(u.user_id)}
                              onChange={() => toggleSelectUser(u.user_id)}
                              size="small"
                              color="default"
                              style={{
                                padding: "2px",
                                color: selectedUserIds.includes(u.user_id)
                                  ? clientColor
                                  : undefined,
                              }}
                            />
                            <span className={classes.userTextWrap}>
                              <span className={classes.userName}>{u.name}</span>
                              <span className={classes.userEmail}>
                                {u.email}
                              </span>
                            </span>
                          </td>
                          <td className={classes.presetCell}>
                            <FormControl fullWidth>
                              <Select
                                value={u.applied_preset_code || ""}
                                displayEmpty
                                disableUnderline
                                className={classes.presetSelect}
                                disabled={!canAssign}
                                MenuProps={{ className: classes.selectMenu }}
                                onChange={(e) =>
                                  applyPresetLocal(u.user_id, e.target.value)
                                }
                                renderValue={(val) =>
                                  val
                                    ? presetLabel(val)
                                    : t("access_management.custom")
                                }
                              >
                                {presets.map((p) => (
                                  <MenuItem
                                    key={p.code}
                                    value={p.code}
                                    classes={{
                                      root: classes.selectMenuItem,
                                      selected: classes.selectMenuItemSelected,
                                    }}
                                  >
                                    {presetLabel(p.code)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </td>
                          {blocksRender.flatMap(({ columns }) =>
                            columns.map((col) => {
                              if (col.type === "summary") {
                                const mark = blockSummaryMark(
                                  col.atoms,
                                  permSet,
                                );
                                return (
                                  <td
                                    key={`sum-${col.block}-${u.user_id}`}
                                    className={cx(
                                      classes.summaryCell,
                                      mark.cls,
                                    )}
                                    onClick={() => toggleBlock(col.block)}
                                  >
                                    {mark.text}
                                  </td>
                                );
                              }
                              if (col.type === "agg") {
                                const st = aggState(col.group, permSet);
                                return (
                                  <td
                                    key={`agg-${col.block}-${col.group.key}-${u.user_id}`}
                                    className={cx(
                                      classes.bodyCell,
                                      classes.groupStartCell,
                                    )}
                                  >
                                    <Checkbox
                                      checked={st.all}
                                      indeterminate={st.some}
                                      disabled={!canAssign}
                                      size="small"
                                      color="default"
                                      style={{
                                        padding: "2px",
                                        color:
                                          st.all || st.some
                                            ? clientColor
                                            : undefined,
                                      }}
                                      onChange={() =>
                                        toggleGroupLocal(
                                          u.user_id,
                                          col.group,
                                          st.all,
                                        )
                                      }
                                    />
                                  </td>
                                );
                              }
                              const checked = permSet.has(col.atom.code);
                              const isSingle =
                                col.group && col.group.atoms.length === 1;
                              return (
                                <td
                                  key={`${col.atom.code}-${u.user_id}`}
                                  className={cx(classes.bodyCell, {
                                    [classes.groupStartCell]: isSingle,
                                  })}
                                >
                                  <Checkbox
                                    checked={checked}
                                    disabled={!canAssign}
                                    size="small"
                                    color="default"
                                    style={{
                                      padding: "2px",
                                      color: checked ? clientColor : undefined,
                                    }}
                                    onChange={() =>
                                      toggleAtomLocal(
                                        u.user_id,
                                        col.atom.code,
                                        checked,
                                      )
                                    }
                                  />
                                </td>
                              );
                            }),
                          )}
                          <td
                            className={classes.scopeCell}
                            onClick={() => canAssign && setScopeUser(u)}
                          >
                            {Object.keys(u.restriction_filters || {}).length >
                            0 ? (
                              <span className={classes.scopeText}>
                                {scopeSummary(u.restriction_filters)}
                              </span>
                            ) : (
                              <span className={classes.scopeEmpty}>
                                {canAssign
                                  ? t("access_management.scope.set")
                                  : "—"}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Bulk action bar */}
            {selectedUserIds.length > 0 && (
              <div className={classes.bulkBar}>
                <span className={classes.bulkLabel}>
                  {t("access_management.selected_count", {
                    count: selectedUserIds.length,
                  })}
                </span>

                {/* Opcao 1: eleger perfil */}
                <FormControl
                  className={cx(
                    classes.selectFormControl,
                    classes.filterSelect,
                  )}
                >
                  <Select
                    value={bulkPreset}
                    displayEmpty
                    disabled={!canAssign}
                    className={classes.filterSelectText}
                    MenuProps={{ className: classes.selectMenu }}
                    onChange={(e) => chooseBulkPreset(e.target.value)}
                    renderValue={(val) =>
                      val
                        ? presetLabel(val)
                        : t("access_management.bulk_choose_preset")
                    }
                  >
                    {presets.map((p) => (
                      <MenuItem
                        key={p.code}
                        value={p.code}
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected,
                        }}
                      >
                        {presetLabel(p.code)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <span className={classes.bulkOr}>
                  {t("access_management.bulk_or")}
                </span>

                {/* Opcao 2: copiar perfil de outro usuario */}
                <FormControl
                  className={cx(
                    classes.selectFormControl,
                    classes.filterSelect,
                  )}
                >
                  <Select
                    value={bulkCopySource}
                    displayEmpty
                    disabled={!canAssign}
                    className={classes.filterSelectText}
                    MenuProps={{ className: classes.selectMenu }}
                    onChange={(e) => chooseBulkCopySource(e.target.value)}
                    renderValue={(val) => {
                      const src = users.find((u) => u.user_id === val);
                      return src
                        ? src.name
                        : t("access_management.bulk_choose_source");
                    }}
                  >
                    {users.map((u) => (
                      <MenuItem
                        key={u.user_id}
                        value={u.user_id}
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected,
                        }}
                      >
                        {u.name}
                        <span className={classes.copyMenuItemPreset}>
                          {presetLabel(u.applied_preset_code)}
                        </span>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  color="client"
                  size="sm"
                  disabled={(!bulkPreset && !bulkCopySource) || !canAssign}
                  onClick={applyBulk}
                >
                  {t("access_management.apply")}
                </Button>
                <Button
                  simple
                  size="sm"
                  className={classes.toolbarTextButton}
                  onClick={clearBulk}
                >
                  {t("access_management.clear_selection")}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </GridItem>

      {scopeUser && (
        <ScopeDialog
          open={Boolean(scopeUser)}
          onClose={() => setScopeUser(null)}
          user={scopeUser}
          onSave={saveScopeLocal}
        />
      )}

      <AuditLogDialog
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        atomByCode={atomByCode}
      />

      <ConfirmDangerousDialog
        open={Boolean(confirmGrants)}
        grants={confirmGrants || []}
        onCancel={() => setConfirmGrants(null)}
        onConfirm={async () => {
          setConfirmGrants(null);
          await doSave();
        }}
      />
    </GridContainer>
  );
}
